import type { ReactNode } from 'react'

import { useI18n } from '@/shared/i18n/I18nProvider'
import {
  resolveLocalizedText,
  type LocalizedTextResolutionPolicy,
  type ResolvedLocalizedText,
} from '@/shared/i18n/localizedText'
import { EmptyValue } from '@/shared/ui/EmptyValue'

import type { StoredLocalizedText } from './localizedDraft'
import { LanguagePill } from './LanguagePill'

/**
 * A localized value READ in the interface language, with the evidence of how
 * it was found.
 *
 * The reading policy is the administrative one: an exact match, else the
 * parent language, else the original, else whatever the value carries. The
 * kernel decides; what is rendered here is its answer and its REASON — when
 * the text shown is not in the reader's language, the pill identifies which
 * language it is in, because a Hebrew reader handed English with no mark
 * would take it for the clinic's choice rather than a gap in the data.
 *
 * THE MARK IS ANNOUNCED WHEREVER THE VALUE IS, AND IS NEVER A CONTROL. It
 * stands in the value's own accessible text — a row's, a heading's, a panel
 * name's — because a reader who is only told the words is told the clinic
 * chose them. What it must not do is put a tab stop or a widget inside that
 * text; `LanguagePill` holds that half and says why.
 *
 * WHAT `renderText` IS FOR is the other half: a registry's name cell opens
 * the record, and the opener is named by what it opens. With the mark inside
 * it a button announced "Complete blood count, shown in English (en)" — the
 * evidence read as part of the record's name. The caller wraps the VALUE in
 * its opener and the mark stays outside, a sibling of the control rather than
 * its content.
 *
 * Patient-facing output is not read through this component: that policy
 * refuses to fall back, and a patient surface asks the kernel directly.
 */
export const ADMINISTRATIVE_READING: LocalizedTextResolutionPolicy = { mode: 'administrative', allowParent: true }

export function resolveLocalizedField(
  value: StoredLocalizedText | null,
  locale: string,
  fallback = '',
): ResolvedLocalizedText {
  return resolveLocalizedText(value, locale, ADMINISTRATIVE_READING, fallback)
}

export interface LocalizedFieldDisplayProps {
  value: StoredLocalizedText | null
  /** What to show when the value is absent. Absent with no fallback renders the shared empty mark. */
  fallback?: string
  /** Wrap only the name in its opener; language evidence stays outside it. */
  renderText?: (text: ReactNode) => ReactNode
}

export function LocalizedFieldDisplay({ value, fallback = '', renderText }: LocalizedFieldDisplayProps) {
  const { locale } = useI18n()
  const resolved = resolveLocalizedField(value, locale, fallback)
  const text = resolved.reason === 'absent' ? <EmptyValue /> : <bdi lang={resolved.actualLanguage ?? undefined}>{resolved.text}</bdi>
  const evidence = resolved.reason !== 'exact' && resolved.actualLanguage !== null
  return (
    <span className="localized-display" data-resolution={resolved.reason}>
      {renderText ? renderText(text) : text}
      {evidence ? <LanguagePill language={resolved.actualLanguage!} /> : null}
    </span>
  )
}
